import { FC, useId } from "react";
import { cn } from "../../lib/utils";
import { Button, Icon, type IconType, Spinner } from "../../primitives";
import { PortaledTooltip } from "../Tooltip";

export type PeriodHeaderActionRowProps = {
	testId?: string;
	label: string;
	iconType: IconType;
	onClick: () => void;
	loading?: boolean;
	/** When set, disables the row and explains on hover what would make it available. */
	disabledReason?: string;
};

export const PeriodHeaderActionRow: FC<PeriodHeaderActionRowProps> = ({
	testId,
	label,
	iconType,
	onClick,
	loading,
	disabledReason,
}) => {
	const tooltipId = useId();
	const inert = loading || Boolean(disabledReason);

	const row = (
		<Button
			variant="ghost"
			data-testid={loading ? `${testId}-loading` : testId}
			className={cn(
				"flex h-auto text-xs items-center justify-start py-3 pl-3 pr-6 bg-surface-base first-of-type:rounded-t last-of-type:rounded-b cursor-pointer border-b border-bc-default last:border-0",
				inert ? "cursor-not-allowed opacity-50" : "hover:bg-surface-light cursor-pointer",
			)}
			disabled={inert}
			onClick={onClick}
			leadingIcon={
				loading ? (
					<Spinner size="lg" className="text-secondary" />
				) : (
					<Icon type={iconType} color="secondary" size="small" />
				)
			}
		>
			{label}
		</Button>
	);

	if (!disabledReason) return row;

	// `[&_button]:pointer-events-none` lets the pointer reach the wrapper so the tooltip fires
	// even though the disabled button is otherwise inert.
	return (
		<span
			tabIndex={0}
			aria-label={disabledReason}
			data-tooltip-id={tooltipId}
			data-tooltip-content={disabledReason}
			className="flex flex-col cursor-not-allowed [&_button]:pointer-events-none"
		>
			{row}
			<PortaledTooltip id={tooltipId} />
		</span>
	);
};
